import { GenexSISModel } from "@/models/sis/GenexSISModel";
import GenexHelperMixins from "@/mixins/GenexHelperMixins";
import { normalize } from "@/utils/data";

export default class ApplicationMediaFields extends GenexSISModel {
  static entity = "application_media_fields";

  static fields() {
    return {
      id: this.attr(null),
      name: this.attr(null),
      tag: this.attr(null),
      description: this.attr(null),
      mandatory: this.attr(null),
      visible: this.attr(null),
      sensitive: this.attr(null),
      attachments: this.attr(null),
    };
  }

  /**
   * @param {Object} pagination Object
   * @param {Number} pagination.page page number
   * @param {Number} pagination.limit page limit
   * @param {Array} relationship array of relationships
   * @returns {Promise} all fields
   */
  static FetchFields({ page = 1, limit = 15 }, query = {}, relationship = []) {
    return this.GenexAxiosGet("ApplicationMediaFields.FetchFields", `/application-media-fields`, {
      params: {
        ...{
          page: page,
          limit: limit,
          with: relationship,
        },
        ...(Object.keys(query).length > 0 ? query : {}),
      },
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }

  /**
   * @param {Object} fields object to store
   * @returns {Promise} stored fileds
   */
  static StoreFields(fields) {
    return this.GenexAxiosPost("ApplicationMediaFields.StoreFields", `/application-media-fields`, fields, {
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }

  /**
   * @param {Object} fields object to store
   * @returns {Promise} stored fileds
   */
  static UpdateFields(fields) {
    return this.GenexAxiosPatch(
      "ApplicationMediaFields.UpdateFields",
      `/application-media-fields/${fields.id}`,
      fields,
      {
        dataTransformer: ({ data: { data } }) => {
          return normalize(data);
        },
      },
    );
  }

  /**
   * @param {Number} id of the field to delete
   * @returns {Promise} deleted field
   */
  static DeleteFields(id) {
    return this.GenexAxiosDelete("ApplicationMediaFields.DeleteFields", `/application-media-fields/${id}`, {
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }

  /**
   * @param {Object} pagination Object
   * @param {Number} pagination.page page number
   * @param {Number} pagination.limit page limit
   * @param {Object} model model path
   * @param {Array} relationship array of relationships
   * @returns {Promise} all media fields
   */
  static FetchFieldsWithMedia({ page = 1, limit = 15 }, model, query = {}, relationship = []) {
    return this.GenexAxiosGet(
      "ApplicationMediaFields.FetchFieldsWithMedia",
      `/application-media-fields/${model.model}/${model.model_id}/media`,
      {
        params: {
          ...{
            page: page,
            limit: limit,
            with: relationship,
          },
          ...(Object.keys(query).length > 0 ? query : {}),
        },

        dataTransformer: ({ data: { data } }) => {
          return normalize(data);
        },
      },
    );
  }

  /**
   * @param {Number} id of the field
   * @returns {Promise} all media for a specific field
   */
  static FetchMediaById(id) {
    return this.GenexAxiosGet("ApplicationMediaFields.FetchMedia", `/application-media-fields/${id}/media`, {
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }

  /**
   * @param {Array} media files to be uploaded
   * @returns {Promise} stored media files
   */
  static StoreMedia(model, media) {
    return this.api().post(
      `/application-media-fields/${model.media_field_id}/${model.model}/${model.model_id}/media`,
      media,
      {
        ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
        headers: {
          ...GenexHelperMixins.methods.DefaultSISHeaders(),
          "content-type": "multipart/form-data",
        },
        dataTransformer: ({ data: { data } }) => {
          return normalize(data);
        },
      },
    );
  }

  /**
   * @param {Object} mediafield object
   * @returns {Promise} zip file
   */
  static DownloadMedia(mediafield) {
    return this.api().get(
      `/application-media-fields/${mediafield.id}/${mediafield.model}/${mediafield.model_id}/media/bulk-download`,
      {
        responseType: "blob",
        ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
      },
    );
  }
}
